import { EditUserTaskItemDialogStore } from './edit-user-task-item-dialog.store';
import { Component, effect, inject, signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { fb, translate } from '../../../../../modules/shared/shared.global-service';
import { FormsModule, Validators } from '@angular/forms';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import {
    InputUserMultiselectComponent,
} from '../../../../../modules/shared/components/inputs/input-user-multiselect/input-user-multiselect.component';
import { FileUploadBoxComponent } from '../../../../kit/file-upload-box/file-upload-box.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { CardComponent } from '../../../../kit/card/card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { EmptyStateComponent } from '../../../../../modules/shared/components/empty-state/empty-state.component';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import {
    UpsertUserTaskItemAppointmentDialogComponent,
} from '../upsert-user-task-item-appointment-dialog/upsert-user-task-item-appointment-dialog.component';
import { TagComponent } from '../../../../kit/tag/tag.component';
import { DeleteButtonComponent } from '../../../../kit/button/delete-button.component';
import { TagsInputComponent } from '../../../../kit/input/tags-input/tags-input.component';
import { IconComponent } from '../../../../kit/icon/icon.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import {
    contentGlobalSuccess,
    successCreated,
    successDeleted,
    successUpdated,
} from '../../../../../modules/content/content.error';
import { UserTaskUpdateInput } from '@libs/types/src/services/input-type/user/user-task-update.input';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { updatePlanFrag } from '../../../folder/individual-plan/individual-plan.query';
import { UserTaskObject } from '@libs/types/src/services/object-type/user/user-task.object';
import { Divider } from '@openng/optimus-ui/divider';
import {
    createTaskEntryFrag,
    getUserTaskAppointmentFrag,
    getUserTaskEntryFrag,
    updateTaskAppointmentFrag,
    updateTaskEntryFrag,
} from './edit-user-task-item-dialog.query';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import { NgTemplateOutlet } from '@angular/common';
import { LinkButtonComponent } from '../../../../kit/button/link-button.component';
import { LoadingComponent } from '../../../../kit/loading/loading.component';
import { UserTaskAppointmentStatus, UserTaskEntryExtendedObject } from './edit-user-task-item-dialog.types';
import { UserTaskEntryCreateInput } from '@libs/types/src/services/input-type/user/user-task-entry-create.input';
import { ConfirmBuilder } from '../../../../../modules/shared/service/confirm.builder';
import { UserTaskEntryUpdateInput } from '@libs/types/src/services/input-type/user/user-task-entry-update.input';
import { FormatRRulePipe } from '../../../../../modules/shared/pipes/format-rrule.pipe';
import { UserTaskAppointmentObject } from '@libs/types/src/services/object-type/user/user-task-appointment.object';
import { AvatarComponent } from '../../../../../modules/shared/components/kit/avatar/avatar.component';
import { PersonInitialsPipe } from '../../../../../modules/shared/pipes/personInitials';
import { FormatPersonPipe } from '../../../../../modules/shared/pipes/format-person.pipe';
import { LocalDatePipe } from '../../../../../modules/shared/pipes/local-date.pipe';

@Component({
    selector: 'app-edit-user-task-item-dialog',
    templateUrl: './edit-user-task-item-dialog.component.html',
    styleUrl: './edit-user-task-item-dialog.component.scss',
    providers: [EditUserTaskItemDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextInputComponent,
        DateInputComponent,
        InputUserMultiselectComponent,
        FileUploadBoxComponent,
        ButtonComponent,
        CardComponent,
        TranslatePipe,
        EmptyStateComponent,
        TagComponent,
        DeleteButtonComponent,
        TagsInputComponent,
        IconComponent,
        Tooltip,
        Divider,
        NgTemplateOutlet,
        LinkButtonComponent,
        LoadingComponent,
        FormsModule,
        FormatRRulePipe,
        AvatarComponent,
        PersonInitialsPipe,
        FormatPersonPipe,
        LocalDatePipe,
    ],
})
export class EditUserTaskItemDialogComponent extends BaseDialogComponent {

    public readonly store = inject(EditUserTaskItemDialogStore);
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);

    protected readonly filesUpload = viewChild(FileUploadBoxComponent);
    protected readonly newEntryInput = viewChild<TextInputComponent>('newEntryInput');

    protected form = new AppFormGroup({
        title: fb.control<string | null>(null, Validators.required),
        dueDate: fb.control<Date | null>(null),
        shareUserIds: fb.control<string[]>([]),
        tags: fb.control<string[]>([]),
        files: fb.control<number[]>([]),
    });

    protected item!: UserTaskObject;
    protected newEntryItem: UserTaskEntryExtendedObject;
    protected lastAffectedItem: WritableSignal<UserTaskEntryExtendedObject | undefined> = signal<UserTaskEntryExtendedObject | undefined>(undefined);

    protected prefix = 'userTasks.detail.';

    constructor() {
        super();

        this.store.onSuccess(
            () => this.fetchEntries(),
            'createUserTaskEntry',
            'updateUserTaskEntry',
            'deleteUserTaskEntry',
        );

        this.store.onSuccess(
            () => this.fetchAppointments(),
            'createUserTaskAppointment',
            'updateUserTaskAppointment',
            'deleteUserTaskAppointment',
        );

        this.item = this.dialogConfig.data.item;

        this.newEntryItem = <UserTaskEntryExtendedObject>{
            id: 0,
            userTaskId: this.item.id,
            userId: this.store.globalGlobalStore.loggedUserStore.currentUserId(),
        };

        this.form.patchValue({
            ...this.item,
            shareUserIds: this.item.shareUsers?.map((it) => it.userId) || [],
            dueDate: this.item.dueDate ? new Date(this.item.dueDate) : null,
        });

        effect(() => {
            this.fetchTags();
            this.fetchAppointments();
            this.fetchEntries();
        });
    }

    public override async save() {
        if (isInvalid(this.form)) return;

        const newFiles = await this.uploadFiles();

        const data = <UserTaskUpdateInput>{
            id: this.item.id,
            title: this.form.value.title,
            dueDate: this.form.value.dueDate?.toISOString() || null,
            tags: this.form.value.tags || [],
            files: [...(this.form.value.files || []), ...newFiles],
            shareUserIds: this.form.value.shareUserIds || [],
        };

        await this.store.api.updateUserTask(data, updatePlanFrag);

        successUpdated();
        await this.filesUpload()?.confirmFiles(newFiles);

        this.dialogRef.close();
    }

    protected editAppointment(appointment: UserTaskAppointmentObject | null = null) {
        this.dialogService.open(UpsertUserTaskItemAppointmentDialogComponent,
            {
                header: translate.instant(`userTasks.detail.appointment.title${appointment ? 'Edit' : 'Create'}`),
                data: {
                    appointment,
                    userTaskId: this.item.id,
                },
            });
    }

    protected replyEntry(item: UserTaskEntryExtendedObject) {
        const user = this.store.globalGlobalStore.loggedUserStore.currentUser();
        const newItem = <UserTaskEntryExtendedObject>{
            id: 0,
            parentId: item.id,
            userTaskId: item.userTaskId,
            userId: user?.id,
            user: user as any,
            isNew: true,
        };

        item.children ? item.children.push(newItem) : item.children = [newItem];

        this.scrollToEntry(newItem);
    }

    protected editEntry(item: UserTaskEntryExtendedObject) {
        item.isEdit = true;
    }

    protected async editEntryConfirm(item: UserTaskEntryExtendedObject) {
        await this.store.api.updateUserTaskEntry(<UserTaskEntryUpdateInput>{
            id: item.id,
            description: item.description,
        }, updateTaskEntryFrag);
    }

    protected deleteEntry(item: UserTaskEntryExtendedObject, event: any) {
        this.confirm.confirm(
            'userTasks.detail.messages.deleteEntryConfirmationLabel',
            null,
            async () => {
                await this.store.api.deleteUserTaskEntry({
                    id: item.id,
                });
                successDeleted();
            },
            null,
            'pi-trash',
            event?.target,
        );
    }

    protected valChange(e: any, item: UserTaskEntryExtendedObject) {
        item.description = e;
    }

    protected async addEntry(item: UserTaskEntryExtendedObject, event: any | null = null) {
        const lastAffectedItem = await this.store.api.createUserTaskEntry(<UserTaskEntryCreateInput>{
            userTaskId: item.userTaskId,
            userId: item.userId,
            parentId: item.parentId,
            description: item.description,
        }, createTaskEntryFrag);

        this.lastAffectedItem.set(lastAffectedItem);

        this.newEntryItem = { ...this.newEntryItem, description: null };
        this.newEntryInput()?.setValue(this.newEntryItem.description);

        if (event) {
            event.srcElement.blur();
        }

        // patch share users if new entry userId is not in list
        const shareUsers = this.form.value.shareUserIds || [];
        if (!(shareUsers).includes(this.lastAffectedItem()!.userId)) {
            this.form.controls.shareUserIds.patchValue([...shareUsers, this.lastAffectedItem()!.userId]);
        }

        successCreated();
    }

    protected toggleCompleteAppointment(item: UserTaskAppointmentObject, event: any) {
        const isCompleted = item.completedAt;
        const prefix = 'userTasks.detail.messages.';

        this.confirm.confirm(
            `${prefix}${isCompleted ? 'uncomplete' : 'complete'}AppointmentConfirmationLabel`,
            null,
            async () => {
                await this.store.api.updateUserTaskAppointment({
                    id: item.id,
                    completedAt: isCompleted ? null : (new Date()).toISOString(),
                }, updateTaskAppointmentFrag);
                contentGlobalSuccess.next(`userTasks.item.${isCompleted ? 'moved' : 'completed'}`);
            },
            null,
            isCompleted ? 'pi-circle' : 'pi-check-circle',
            event?.target,
        );
    }

    protected async deleteAppointment(item: UserTaskAppointmentObject, event: any) {
        this.confirm.confirm(
            'userTasks.detail.messages.deleteAppointmentConfirmationLabel',
            null,
            async () => {
                await this.store.api.deleteUserTaskAppointment({
                    id: item.id,
                });
                successDeleted();
            },
            null,
            'pi-trash',
            event?.target,
        );
    }

    protected scrollToEntry(item: UserTaskEntryExtendedObject) {
        setTimeout(() => {
            const id = `entry_${item.parentId || 0}_${item.id}`;
            const itemToScrollTo = document.getElementById(id);
            itemToScrollTo?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        });
    }

    protected scrollToEnd() {
        setTimeout(() => {
            const entriesContainer = document.getElementById('entries-container');
            entriesContainer?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        });
    }

    private async uploadFiles() {
        const metadata = {
            categoryCode: DefaultCategoryTypeEnum.USER_TASK,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: [
                {
                    externalType: ExternalType.folder,
                    externalId: String(this.item.folderId),
                },
                {
                    externalType: ExternalType.service,
                    externalId: String(this.store.globalGlobalStore.loggedUserStore.selectedServiceId()),
                },
            ],
        };

        return this.filesUpload()?.uploadFiles(metadata) || [];
    }

    private async fetchTags() {
        await this.store.api.getUserTaskUserTags({
            userId: this.item.userId,
            folderId: this.item.folderId,
        });
    }

    private async fetchEntries() {
        await this.store.api.getUserTaskEntries(FilterBuilder.singlePage({
            userTaskId: this.item.id,
            sort: { column: 'createdAt', direction: 'ASC' },
        }), getUserTaskEntryFrag);

        if (this.lastAffectedItem()) {
            this.scrollToEntry(this.lastAffectedItem()!);
            this.lastAffectedItem.set(undefined);
        } else {
            this.scrollToEnd();
        }
    }

    private async fetchAppointments() {
        await this.store.api.getUserTaskAppointments(FilterBuilder.singlePage({
            userTaskId: this.item.id,
            sort: { column: 'createdAt', direction: 'ASC' },
        }), getUserTaskAppointmentFrag);
    }

    protected readonly UserTaskAppointmentStatus = UserTaskAppointmentStatus;
}
